import { useState, useEffect } from 'react';
import { getPortfolioLive } from '../lib/cerebro';

const formatUSD = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function HealthGauge({ score }) {
  // Color basado en score
  const color = score >= 80 ? '#16A34A' : score >= 60 ? '#F59E0B' : '#DC2626';
  const label = score >= 80 ? 'Excelente' : score >= 60 ? 'Aceptable' : 'Necesita atencion';
  const pct = Math.min(100, Math.max(0, score));

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-14 h-14 flex-shrink-0">
        <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#F3F4F6" strokeWidth="3" />
          <circle
            cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${pct * 0.975} 100`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-extrabold" style={{ color }}>{score}</span>
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-[#181D27]">Salud del portafolio</div>
        <div className="text-xs" style={{ color }}>{label}</div>
      </div>
    </div>
  );
}

function AlertItem({ alert }) {
  const iconMap = {
    significant: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
    moderate: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
    extra: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  };
  const style = iconMap[alert.type] || iconMap.moderate;

  return (
    <div className={`flex items-start gap-2.5 p-2.5 rounded-lg ${style.bg} border ${style.border}`}>
      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${style.dot}`} />
      <div className={`text-xs ${style.text} leading-relaxed`}>{alert.message}</div>
    </div>
  );
}

function SuggestionItem({ suggestion }) {
  const isReduce = suggestion.action === 'reducir';
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#F5F5F5] last:border-b-0">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white ${isReduce ? 'bg-red-500' : 'bg-green-500'}`}>
          {isReduce ? '-' : '+'}
        </div>
        <div>
          <span className="text-sm font-bold text-[#2A59C6]">{suggestion.ticker}</span>
          <span className="text-xs text-[#6B7280] ml-1.5">{isReduce ? 'Reducir' : 'Aumentar'}</span>
        </div>
      </div>
      <span className="text-sm font-semibold text-[#181D27]">~{formatUSD(suggestion.amount)}</span>
    </div>
  );
}

export default function PortfolioLiveWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getPortfolioLive();
        if (!cancelled) {
          if (result) {
            setData(result);
          } else {
            setError('no-data');
          }
        }
      } catch (e) {
        console.error('[PortfolioLive] Widget error:', e);
        if (!cancelled) setError(e.message || 'unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#F3F4F6]" />
          <div className="flex-1">
            <div className="h-4 bg-[#F3F4F6] rounded w-40 mb-2" />
            <div className="h-3 bg-[#F3F4F6] rounded w-24" />
          </div>
        </div>
      </div>
    );
  }

  // Sin datos — no mostrar el widget (fallback silencioso, sin errores feos al cliente)
  if (error || !data) {
    return null;
  }

  const { summary, alerts, positions, rebalanceSuggestions, deviations } = data;
  const hasAlerts = alerts && alerts.length > 0;
  const hasSuggestions = rebalanceSuggestions && rebalanceSuggestions.length > 0;
  const hasPositions = positions && positions.length > 0;

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden mb-4">
      {/* Header — siempre visible (basic tier) */}
      <div
        className="p-5 cursor-pointer hover:bg-[#FAFAFA] transition"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between mb-3">
          <HealthGauge score={summary.healthScore} />
          <div className="text-right">
            <div className="text-lg font-extrabold text-[#181D27]">{formatUSD(summary.totalValue)}</div>
            <div className={`text-xs font-semibold ${summary.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {summary.unrealizedPL >= 0 ? '+' : ''}{formatUSD(summary.unrealizedPL)}
              <span className="text-[#9CA3AF] ml-1">
                ({summary.unrealizedPLPct >= 0 ? '+' : ''}{(summary.unrealizedPLPct * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            summary.isBalanced
              ? 'bg-green-50 text-green-700'
              : 'bg-amber-50 text-amber-700'
          }`}>
            {summary.status}
          </div>
          <div className="flex items-center gap-1 text-xs text-[#9CA3AF]">
            <span>{summary.totalPositions} posiciones</span>
            <span className="ml-1">{data.dataSource === 'mock' ? '(demo)' : ''}</span>
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable detail (intermediate+ tiers) */}
      {expanded && (
        <div className="border-t border-[#F3F4F6] px-5 pb-5">
          {/* Alerts */}
          {hasAlerts && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-[#181D27] mb-2">Alertas</div>
              <div className="space-y-2">
                {alerts.map((a, i) => <AlertItem key={i} alert={a} />)}
              </div>
            </div>
          )}

          {/* Positions */}
          {hasPositions && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-[#181D27] mb-2">Posiciones</div>
              <div className="space-y-1.5">
                {positions.map((p) => (
                  <div key={p.symbol} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-[#F5F5F5] flex items-center justify-center text-[10px] font-bold text-[#2A59C6]">
                        {p.symbol.slice(0, 2)}
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-[#181D27]">{p.symbol}</span>
                        <span className="text-xs text-[#9CA3AF] ml-1.5">{(p.weight * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-[#181D27]">{formatUSD(p.marketValue)}</div>
                      <div className={`text-[11px] font-semibold ${p.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {p.unrealizedPL >= 0 ? '+' : ''}{formatUSD(p.unrealizedPL)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rebalance suggestions */}
          {hasSuggestions && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-[#181D27] mb-2">Sugerencias de rebalanceo</div>
              <div className="bg-[#F9FAFB] rounded-xl p-3">
                {rebalanceSuggestions.map((s, i) => <SuggestionItem key={i} suggestion={s} />)}
              </div>
              <div className="text-[11px] text-[#9CA3AF] mt-2 text-center">
                Estas son sugerencias educativas, no recomendaciones de inversion
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
